import { CreationLayout } from './CreationLayout';

interface ConfirmStageProps {
  name: string;
  loading: boolean;
  error: string | null;
  onBack: () => void;
  onConfirm: () => void;
}

export function ConfirmStage({
  name,
  loading,
  error,
  onBack,
  onConfirm,
}: ConfirmStageProps): JSX.Element {
  return (
    <CreationLayout stageKey="confirm" onBack={onBack} backLabel="Back">
      <div className="px-6 sm:px-10 pt-10 pb-2 text-center">
        <p className="text-candle-dark text-[0.7rem] uppercase tracking-[0.4em] mb-3">The Oath</p>

        <div className="my-6 px-2">
          <div className="text-ink-light text-xs uppercase tracking-[0.3em] mb-1">Henceforth</div>
          <div
            className="font-display text-4xl sm:text-5xl text-ink leading-tight break-words"
            aria-live="polite"
          >
            {name}
          </div>
          <div className="mx-auto mt-4 flex items-center justify-center gap-3 text-candle/70">
            <span aria-hidden className="h-px w-12 bg-candle/40" />
            <span aria-hidden>&diams;</span>
            <span aria-hidden className="h-px w-12 bg-candle/40" />
          </div>
        </div>

        <h2 className="font-display text-2xl sm:text-3xl text-ink mb-4">Are you ready?</h2>

        <div className="space-y-3 text-ink-mid text-base leading-relaxed text-balance">
          <p>
            The harbormaster takes your name without looking up. The dock creaks beneath your boots.
            Somewhere ahead, an old man waits with a job offer and a knowing smile.
          </p>
          <p className="text-ink font-display tracking-wider">Your tale begins.</p>
        </div>
      </div>

      {error && (
        <div className="px-6 sm:px-10 pt-2">
          <div
            role="alert"
            className="rounded-md border border-blood-bright/40 bg-blood-bright/10 px-3 py-2 text-sm text-blood"
          >
            {error}
          </div>
        </div>
      )}

      <div className="px-6 sm:px-10 pt-6 pb-8 space-y-3">
        <button
          type="button"
          onClick={onConfirm}
          disabled={loading}
          className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-sea-deep text-candle border border-candle/40 hover:bg-sea-dark hover:border-candle px-5 py-3 font-display tracking-[0.2em] uppercase text-sm transition-colors min-h-11 disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-candle focus:ring-offset-2 focus:ring-offset-parchment"
        >
          {loading && (
            <span
              aria-hidden
              className="inline-block h-3.5 w-3.5 rounded-full border-2 border-candle/40 border-t-candle animate-spin"
            />
          )}
          <span>{loading ? 'Setting sail…' : 'Set Sail'}</span>
        </button>
        <button
          type="button"
          onClick={onBack}
          disabled={loading}
          className="w-full inline-flex items-center justify-center rounded-md border border-ink/20 bg-parchment-light text-ink-mid hover:border-candle hover:text-ink px-5 py-3 font-display tracking-[0.15em] uppercase text-xs transition-colors min-h-11 disabled:opacity-60"
        >
          Choose Different Name
        </button>
      </div>
    </CreationLayout>
  );
}
